import { IconSquareArrowUpRightOutline18 as ExternalLink } from "nucleo-ui-outline-18"

import { Button, IntegrationTemplate, ZapierIcon } from "@productlane/ui"

import { MetaInfo } from "@/components/meta-info"
import { IntegrationLayout } from "./integration-layout"

const ZAPIER_TITLE = "Zapier"
const ZAPIER_DESCRIPTION =
	"Connect countless tools with Productlane via Zapier."

const ZAPIER_LONG_DESCRIPTION = (
	<div>
		<div className="mb-8">
			<div className="mb-2 text-lg font-semibold text-primary">
				How the Zapier Integration Works
			</div>
			<div className="mb-6 text-sm leading-relaxed text-secondary">
				Connect Productlane to thousands of apps through Zapier to automate your
				customer support workflow. Create automated workflows (Zaps) that
				capture customer support requests from forms, emails, surveys, and other
				tools, sending them directly to Productlane.
			</div>
		</div>

		<div className="mb-8">
			<div className="mb-2 text-base font-semibold text-primary">
				What You Can Connect
			</div>
			<div className="mb-4 space-y-2 text-sm text-secondary">
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Forms:</strong> Typeform, Google
						Forms, Tally, and Jotform responses.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Email:</strong> Gmail, Outlook, and
						other email providers for support tickets.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">CRMs:</strong> Salesforce,
						Pipedrive, and other CRM systems.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Surveys:</strong> SurveyMonkey,
						Typeform, and customer satisfaction tools.
					</span>
				</div>
			</div>
		</div>

		<div className="mb-8">
			<div className="mb-2 text-base font-semibold text-primary">
				Setup Instructions
			</div>
			<div className="mb-4 space-y-3 text-sm text-secondary">
				<div className="flex gap-2">
					<span className="font-medium text-primary">1.</span>
					<div>
						<a
							className="inline-flex items-center gap-1 text-secondary underline underline-offset-4 transition-all hover:decoration-green-500"
							target="_blank"
							rel="noreferrer"
							href="/settings/integrations/api"
						>
							Create a new API key
						</a>{" "}
						in Productlane&apos;s API settings.
					</div>
				</div>
				<div className="flex gap-2">
					<span className="font-medium text-primary">2.</span>
					<div>
						Visit{" "}
						<a
							className="inline-flex items-center gap-1 text-secondary underline underline-offset-4 transition-all hover:decoration-green-500"
							target="_blank"
							rel="noreferrer"
							href="https://zapier.com/apps/productlane/integrations"
						>
							Zapier&apos;s Productlane integrations
						</a>{" "}
						and create a new Zap.
					</div>
				</div>
				<div className="flex gap-2">
					<span className="font-medium text-primary">3.</span>
					<span>Authenticate with Productlane using your API key.</span>
				</div>
				<div className="flex gap-2">
					<span className="font-medium text-primary">4.</span>
					<span>Connect your desired apps and configure the workflow.</span>
				</div>
			</div>
		</div>

		<div className="mb-8">
			<div className="mb-2 text-base font-semibold text-primary">
				Common Use Cases
			</div>
			<div className="mb-4 space-y-2 text-sm text-secondary">
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Form Submissions:</strong>{" "}
						Automatically create support threads from form responses.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Email Support:</strong> Turn
						support emails into Productlane threads.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">CRM Integration:</strong> Sync
						customer data and support requests.
					</span>
				</div>
				<div className="flex gap-2">
					<span className="text-primary">•</span>
					<span>
						<strong className="text-primary">Survey Feedback:</strong> Capture
						customer feedback and bug reports.
					</span>
				</div>
			</div>
		</div>

		<div className="mb-6">
			<div className="mb-2 text-base font-semibold text-primary">
				No Technical Knowledge Required
			</div>
			<div className="text-sm leading-relaxed text-secondary">
				Zapier provides an easy, no-code solution for automation. Simply select
				your trigger app, configure the action to create a thread in
				Productlane, and map the fields. Your workflows will run automatically
				in the background.
			</div>
		</div>
	</div>
)

export function ZapierIntegration() {
	return (
		<>
			<MetaInfo title="Zapier Integration" />
			<IntegrationLayout />
			<IntegrationTemplate
				name={ZAPIER_TITLE}
				description={ZAPIER_DESCRIPTION}
				button={
					<Button
						Icon={ExternalLink}
						onClick={() => {
							window.open(
								"https://zapier.com/apps/productlane/integrations",
								"_blank",
							)
						}}
					>
						Activate integration
					</Button>
				}
				instructions={ZAPIER_LONG_DESCRIPTION}
				logo={<ZapierIcon className="w-10" />}
			/>
		</>
	)
}
